/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { HttpErrorResponse } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { WINDOW } from '../helpers/window.injectToken'
import { PidxDeviceDetectionService } from '../pidx-device-detection/pidx-device-detection.service'

import { OneGaManager } from './one-ga-manager'
import { OneGaTrackHandler } from './one-ga-track-handler'
import { ExtendedContext, ExtendedVisitor, GTMConfig } from './pidx-google-tag-manager-integration.interfaces'
import { ComponentClick, OneGaData, PageExperience } from './one-ga-types'

const EVENT_ACTION_PREFIX = 'PAGMyPorsche_'
const LOAD_EVENT_ACTION_SUFFIX = '_Pageload'
const CLICK_EVENT_ACTION_SUFFIX = '_Click'
const LOAD_EVENT_PAGE_NAMES: Record<string, string> = {
  // eslint-disable-next-line camelcase
  personal_data: 'PersonalData',
  paymentmethods: 'PaymentMethods',
}
const DEVICES = {
  MOBILE: 'mobile',
  DESKTOP: 'desktop',
  TABLET: 'tablet',
}

@Injectable({
  providedIn: 'root',
})
export class PidxGoogleTagManagerIntegrationService {
  private pidxDeviceDetectionService = inject(PidxDeviceDetectionService)
  private oneGaTrackHandler = inject(OneGaTrackHandler)
  private oneGaManager = inject(OneGaManager)
  private window = inject<Window>(WINDOW)

  private gtmEnabled!: boolean
  private pagData: any

  private config!: GTMConfig
  private context!: ExtendedContext
  private visitor!: ExtendedVisitor
  private page!: PageExperience

  private formValidationStore: Record<string, Set<string>> = {}

  public init(config: GTMConfig, enabled = false, containerId: string) {
    this.config = config
    this.gtmEnabled = enabled

    if (!this.gtmEnabled) {
      return
    }

    this.oneGaManager.init(containerId, {
      context: this.config,
    })

    this.pagData = (this.window as any).pagData
    const language = this.config.language.split('_')

    this.context = {
      applicationId: this.config.applicationId,
      environment: this.config.environment,
      country: this.config.country.toUpperCase(),
      language: language[0],
      server: this.config.server,
    }

    this.visitor = {
      loginStatus: true,
      deviceManufacturer: this.window.navigator.vendor,
      useragent: this.window.navigator.userAgent,
      deviceType: this.pidxDeviceDetectionService.isMobileDevice()
        ? this.pidxDeviceDetectionService.isMobilePhoneDevice()
          ? DEVICES.MOBILE
          : DEVICES.TABLET
        : DEVICES.DESKTOP,
    }

    this.visitor = this.getUpdatedDeviceData()
    this.page = this.getUpdatedRouteData()
  }

  public triggerPageLoad(overwriteOptions?: any, error?: HttpErrorResponse) {
    if (!this.gtmEnabled) {
      return
    }
    let eventAction =
      EVENT_ACTION_PREFIX + (LOAD_EVENT_PAGE_NAMES[this.page.pageName ?? ''] ?? this.page.pageName) + LOAD_EVENT_ACTION_SUFFIX

    this.visitor = this.getUpdatedDeviceData()
    this.page = this.getUpdatedRouteData()

    if (overwriteOptions && overwriteOptions.pageName) {
      this.page.pageName = overwriteOptions.pageName
    }

    if (overwriteOptions && overwriteOptions.eventAction) {
      eventAction = overwriteOptions.eventAction
    }
    const gtmData: OneGaData = {
      pageExperience: this.page,
      visitor: this.visitor,
      context: {
        ...this.context,
        timestamp: this.getTimestamp(),
        eventAction,
      },
    }

    if (error) {
      gtmData.pageExperience = {
        ...gtmData.pageExperience,
        errorCode: error.status.toString(),
        errorMessage: error.message,
        errorReferringUrl: this.page.pageId,
      }
    }

    this.oneGaTrackHandler.track(gtmData)
  }

  public triggerClickTracking(componentClickData: ComponentClick, error?: HttpErrorResponse) {
    if (!this.gtmEnabled) {
      return
    }

    this.getUpdatedDeviceData()
    this.getUpdatedRouteData()

    const eventAction =
      EVENT_ACTION_PREFIX + (LOAD_EVENT_PAGE_NAMES[this.page.pageName ?? ''] ?? this.page.pageName) + CLICK_EVENT_ACTION_SUFFIX

    const gtmData: OneGaData = {
      pageExperience: this.page,
      visitor: this.visitor,
      context: {
        ...this.context,
        timestamp: this.getTimestamp(),
        eventAction,
      },
      componentClick: {
        ...componentClickData,
      },
    }

    if (error) {
      gtmData.pageExperience = {
        ...gtmData.pageExperience,
        errorCode: error.status.toString(),
        errorMessage: error.message,
        errorReferringUrl: this.page.pageId,
      }
    }

    this.oneGaTrackHandler.track(gtmData)
  }

  public trackInvalidInputEntry(formName: string) {
    if (!this.gtmEnabled) {
      return
    }

    this.visitor = this.getUpdatedDeviceData()
    this.page = this.getUpdatedRouteData()

    const componentFormErrors = [...(this.formValidationStore[formName]?.values() ?? [])].map((fieldName) => {
      return { fieldName }
    })

    const gtmData: OneGaData = {
      pageExperience: this.page,
      visitor: this.visitor,
      context: {
        ...this.context,
        timestamp: this.getTimestamp(),
        eventAction: 'PAGMyPorsche_InvalidEntry_Load',
      },
      componentForm: {
        errors: componentFormErrors,
      },
    }

    this.oneGaTrackHandler.track(gtmData)
  }

  public setupValidationError(formName: string) {
    this.formValidationStore[formName] = new Set()
  }

  public clearValidationErrors(formName: string) {
    delete this.formValidationStore[formName]
  }

  public hasValidationErrors(formName: string): boolean {
    if (!formName || typeof this.formValidationStore[formName] === 'undefined') {
      return false
    }

    return !!this.formValidationStore[formName].size
  }

  public addValidationError(formName: string, formControlName: string) {
    if (!this.formValidationStore[formName]?.has(formControlName)) {
      this.formValidationStore[formName]?.add(formControlName)
    }
  }

  public removeValidationError(formName: string, formControlName: string) {
    this.formValidationStore[formName]?.delete(formControlName)
  }

  public cleanUpPageUrl(url: string): string {
    // To prevent sending sensitive data to the google tag manager we remove the hash and the search.
    // In case any of the hashes or search params is needed this implementation needs to be adjusted accordingly
    const urlObj = new URL(url)
    urlObj.hash = ''
    urlObj.search = ''

    return urlObj.href
  }

  public getUpdatedDeviceData() {
    return {
      ...this.visitor,
      deviceBrowserHeight: this.window.innerHeight.toString(),
      deviceBrowserWidth: this.window.innerWidth.toString(),
      deviceBrowserOrientation: this.window.matchMedia('(orientation: portrait)').matches ? 'p' : 'l',
    }
  }

  public getUpdatedRouteData() {
    return {
      pageId: this.cleanUpPageUrl(this.window.location.href),
      pageName: (new URL(this.window.location.href).pathname.split('/').pop() ?? '').replace('-', '_'),
    }
  }

  public getTimestamp() {
    const pad = (num: number) => {
      let s = num + ''
      while (s.length < 2) {
        s = '0' + s
      }
      return s
    }

    const date = new Date()
    const offset = date.getTimezoneOffset() * -1

    const offsetSign = offset >= 0 ? '+' : '-'
    const formattedOffset = `${pad(Math.floor(offset / 60))}:${pad(offset % 60)}`

    return `${date.toISOString().substring(0, 19)}${offsetSign}${formattedOffset}`
  }
}
